/**
 * Navigation Module
 * Handles mobile menu toggle, accessibility states, keyboard navigation,
 * smooth scrolling, and scrollspy with fallback.
 */

export function initNavigation() {
  const menuButton = document.getElementById('menuButton');
  const mainNav = document.getElementById('mainNav');
  const navLinks = document.querySelectorAll('.nav-links a');
  const sections = document.querySelectorAll('main section[id]');

  if (!menuButton || !mainNav) return;

  // Toggle mobile navigation menu
  function toggleMenu(forceClose = false) {
    const isCurrentlyOpen = mainNav.classList.contains('open');
    const shouldOpen = forceClose ? false : !isCurrentlyOpen;

    mainNav.classList.toggle('open', shouldOpen);
    menuButton.setAttribute('aria-expanded', String(shouldOpen));
    menuButton.setAttribute('aria-label', shouldOpen ? 'Close navigation' : 'Open navigation');
    menuButton.textContent = shouldOpen ? '✕ Close' : '☰ Menu';

    if (shouldOpen) {
      // Focus first link in menu for accessibility
      const firstLink = mainNav.querySelector('a');
      if (firstLink) firstLink.focus();
    }
  }

  menuButton.addEventListener('click', () => toggleMenu());

  // Close menu on link click
  navLinks.forEach((link) => {
    link.addEventListener('click', () => {
      if (mainNav.classList.contains('open')) {
        toggleMenu(true);
      }
    });
  });

  // Close menu on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && mainNav.classList.contains('open')) {
      toggleMenu(true);
      menuButton.focus();
    }
  });

  // Close menu on outside click
  document.addEventListener('click', (e) => {
    if (
      mainNav.classList.contains('open') &&
      !mainNav.contains(e.target) &&
      !menuButton.contains(e.target)
    ) {
      toggleMenu(true);
    }
  });

  // Scrollspy Implementation
  // modern-web-guidance: use scroll-target-group if supported, fallback to IntersectionObserver
  const supportsScrollTargetGroup = window.CSS && CSS.supports && CSS.supports('scroll-target-group: auto');

  if (supportsScrollTargetGroup) {
    const syncAriaCurrent = () => {
      const currentLink = document.querySelector('nav a:target-current');
      navLinks.forEach((link) => {
        const isCurrent = link === currentLink;
        link.setAttribute('aria-current', isCurrent ? 'true' : 'false');
      });
    };
    syncAriaCurrent();
    document.addEventListener('scrollend', syncAriaCurrent);
  } else {
    // Robust IntersectionObserver Fallback for Safari, Firefox, and older engines
    if ('IntersectionObserver' in window && sections.length > 0) {
      const observerOptions = {
        root: null,
        rootMargin: '-20% 0px -60% 0px',
        threshold: 0
      };

      const sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('id');
            navLinks.forEach((link) => {
              const href = link.getAttribute('href');
              const isActive = href === `#${id}`;
              link.classList.toggle('active', isActive);
              link.setAttribute('aria-current', isActive ? 'true' : 'false');
            });
          }
        });
      }, observerOptions);

      sections.forEach((section) => sectionObserver.observe(section));
    }
  }
}

